"use client"

import { cva } from "class-variance-authority"
import { type HTMLMotionProps, motion } from "motion/react"

import { cn } from "@/lib/cn"

const morphingSquareVariants = cva("flex gap-2 items-center justify-center", {
  variants: {
    messagePlacement: {
      bottom: "flex-col",
      top: "flex-col-reverse",
      right: "flex-row",
      left: "flex-row-reverse",
    },
  },
  defaultVariants: {
    messagePlacement: "bottom",
  },
})

export interface MorphingSquareProps {
  message?: string
  /**
   * Position of the message relative to the spinner.
   * @default bottom
   */
  messagePlacement?: "top" | "bottom" | "left" | "right"
}

export function MorphingSquare({
  className,
  message,
  messagePlacement = "bottom",
  ...props
}: HTMLMotionProps<"div"> & MorphingSquareProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(morphingSquareVariants({ messagePlacement }))}
    >
      <motion.div
        // Same indigo → violet gradient as the "Add Entry" button; reads on light and dark.
        className={cn(
          "h-10 w-10 bg-gradient-to-br from-[#5B47E8] to-[#8B5CF6] shadow-lg shadow-[#5B47E8]/30 dark:shadow-[#8B5CF6]/30",
          className,
        )}
        animate={{
          borderRadius: ["6%", "50%", "6%"],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 2,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
        {...props}
      />
      {message && (
        <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
          {message}
        </div>
      )}
    </div>
  )
}

export default MorphingSquare
